/**
 * Glass material tokens — the dark-glass treatment shared across every in-chat
 * pill / banner (the onboarding → plan sequence + the reskin rollout; see
 * docs/glass-reskin-checklist.md).
 *
 * Lives in utils/ (NOT a `.native` file) so SHARED components — e.g. the unified
 * recal strip, which RN-Web renders on both platforms — can import it without a
 * platform-specific import.
 */

// ── Scalar tokens ────────────────────────────────────────────────────────────
import { CoachCedarColors } from './styleConverter';

export const GLASS_BG = '#113C2B'; // darkCedar — the glass surface
export const GLASS_BORDER = 'rgba(255,255,255,0.2)'; // luminous hairline edge
export const GLASS_TEXT = 'rgba(255,255,255,0.92)'; // primary text / active glyphs
export const GLASS_TEXT_DIM = 'rgba(255,255,255,0.45)'; // muted / upcoming
// Secondary ink on glass: the chat pills' dimmed line, named for the quick views
// (2026-09-27). 5.4:1 on the dialog glass (#143E2D), 4.7:1 on a GLASS_LIFT_BG
// surface — AA for body text on both, where GLASS_TEXT_DIM (3.7:1) is not.
export const GLASS_TEXT_SECONDARY = 'rgba(255,255,255,0.60)';

// ── Spreadable style fragments ───────────────────────────────────────────────
// The soft float shadow that lifts the glass off the light chat bg (iOS shadow*
// + Android elevation). Spread onto a container style.
export const GLASS_SHADOW = {
  shadowColor: '#000',
  shadowOffset: { width: 0, height: 4 },
  shadowOpacity: 0.15,
  shadowRadius: 14,
  elevation: 8,
};

// The full glass-card chrome: bg + luminous hairline + radius + float shadow.
// Spread onto a container and add your own padding/layout, e.g.
//   container: { ...GLASS_CARD, padding: 14 }
export const GLASS_CARD = {
  backgroundColor: GLASS_BG,
  borderWidth: 1,
  borderColor: GLASS_BORDER,
  borderRadius: 6,
  ...GLASS_SHADOW,
};

// ── The ladder (Health Glass Sprint S0, 2026-07-22) ─────────────────────────
// Three glass families shipped before this sprint with forked values — chat
// pills (hairline .2, radius 6), dialogs (hairline .12, radius 16-20,
// AppAlertModal/summer50 family), auth-wall white-alpha lifts (.06-.09 fills).
// The ladder names each scale so section surfaces (Health is the pilot) draw
// from ONE set:
//   chip   → GLASS_CARD          (radius 6,  hairline .2,  shadow 0,4/.15/14)
//   card   → GLASS_CARD_LG       (radius 16, hairline .12, shadow 0,6/.25/14)
//   dialog → (AppAlertModal family, radius 20, shadow 0,8/.35/20 — unchanged)
// Two MODES, keyed by what's underneath: on a LIGHT canvas glass is the dark
// card (GLASS_CARD_LG); on a DARK hero glass is a white-alpha lift (GLASS_LIFT,
// the auth-wall family). Foreground rules are unchanged: white text, thrive
// accents, never cedarPrime on glass.

export const GLASS_HAIRLINE_SOFT = 'rgba(255,255,255,0.12)'; // card/dialog edge (summer50 value)
export const GLASS_DIVIDER = 'rgba(255,255,255,0.09)'; // hairline INSIDE glass
export const GLASS_LIFT_BG = 'rgba(255,255,255,0.07)'; // nested surface ON glass (minis, chips)
// A neutral chip ON glass: the quick view's status badge for anything that is not
// live (Matt's pick, 2026-09-27: white .92 ink on it is 7.83:1). Named when the
// quick view moved to the theme; it was the literal in PeekModal until then.
export const GLASS_CHIP_BG = 'rgba(255,255,255,0.10)';
// An error mark ON glass: the alert's warning triangle. The page's reds are too
// dark to read on dark cedar, so the glass family uses this pale one.
export const GLASS_ERROR_INK = '#FCA5A5';

// Data-ink on glass is thrive (5.9:1 on darkCedar). Its light-surface partner
// is cedarPrime (4.9:1 on white) — stream-teal measured 2.93:1 on white, below
// the 3:1 floor, and teal is old palette (discontinued app-wide, 2026-07-22).
export const GLASS_DATA_INK = '#3BD49B'; // thrive — sparkline/marks on glass

// MODE A — dark card on a LIGHT canvas (In Focus cards, the drawer hero).
export const GLASS_CARD_LG = {
  backgroundColor: GLASS_BG,
  borderWidth: 1,
  borderColor: GLASS_HAIRLINE_SOFT,
  borderRadius: 16,
  shadowColor: '#000',
  shadowOffset: { width: 0, height: 6 },
  shadowOpacity: 0.25,
  shadowRadius: 14,
  elevation: 8,
};

// MODE B — white-alpha lift on a DARK hero (the auth-wall family, e.g. the
// Health Today composite floating on the section hero gradient).
export const GLASS_LIFT = {
  backgroundColor: GLASS_LIFT_BG,
  borderWidth: 1,
  borderColor: 'rgba(255,255,255,0.14)',
  borderRadius: 16,
};

// ── DAYLIGHT (chosen full-app direction, founder 2026-07-23) ─────────────────
// "Light room, dark instruments." Pages keep a light canvas; each screen has
// ONE dark-glass instrument (GLASS_CARD_LG) whose section aurora glows INSIDE
// the card; secondary content is FROST (light glass). Section identity comes
// from light — an in-card aurora + soft canvas blobs — never from hue-blending
// glass surface hexes. Mockups: Headwater (mock 1) vs Daylight (mock 2, chosen).
// Rules recap (gate-enforced in tests/gate/test_daylight_material_rules.py):
//   • one dark instrument per screen; secondary = frost
//   • every instrument is the SAME material — light (InstrumentSheen), not hue
//   • multi-hue data lives on a light panel, even inside the instrument
//   • Heat (#D0603E) stays semantic (warnings/intensity) — never a section tint

// Sheet/drawer altitude — deeper than any page glass (brand "Roots").
export const GLASS_ROOTS = '#12402F';

// Scrim under every dialog/sheet — the altitude separator.
export const SCRIM = 'rgba(11,22,20,0.70)';

// AA-safe inks for the section accents on LIGHT surfaces (eyebrows, labels,
// counts). The raw brand accents fail 4.5:1 on white (Drive 3.55, Thrive-mint
// 1.9, Reset 3.66) — same lesson as thrive/cedarPrime. Fills/glows keep the
// full-saturation values; TEXT uses these.
export const DAYLIGHT_ACCENT_INK = {
  drive: '#C7402F',  // 4.87:1 on #FCFCFC
  thrive: '#16825C', // 4.67:1
  reset: '#6F5AA8',  // 5.53:1
  roots: '#4E6459',  // 5.0:1
} as const;

// FROST — the secondary light-glass material. Deliberately blur-free: this is
// the SHIPPABLE recipe (translucent white + bright top hairline + soft float),
// reading as glass because the canvas blobs show through. Do NOT add
// backdrop/expo-blur per-row — per-row live blur in scrolling lists is a perf
// regression; real blur is reserved for static chrome (sticky bars, sheets).
/**
 * CEDAR_BRIGHT — the bright end of every Daylight green gradient.
 *
 * Ceiling is CONTRAST, not taste: this is the BRIGHTEST green that keeps
 * white body text at or above the 4.5:1 AA floor (4.56:1 measured).
 * `thrive` (#3BD49B) is the tempting choice and measures 1.90:1 — a hard
 * failure. Do not raise this toward thrive without also moving the inks
 * to dark. TestCtaGradientIsAccessible computes the ratio live.
 *
 * Lives here, NOT as a local const per component: the Daylight census found
 * NINE bespoke segmented strips that had diverged precisely because each
 * owned its own copy of the material. One token, one appearance.
 */
export const CEDAR_BRIGHT = '#1D8658';

/**
 * CTA_GRADIENT — the green gradient shared by the Next Week reveal hero and
 * the subscription CTA family (founder 2026-08-02: "I like the next week card
 * gradient, can we do this too for the Manage Subscription button ... same for
 * start trial").
 *
 * Diagonal top-left → bottom-right, matching the reveal card exactly so the
 * two never read as different materials.
 *
 * ⚠️ PRESS STATE: an opaque gradient paints OVER `backgroundColor`, so a
 * pressed-fill (the old `backgroundColor: thrive`) is invisible under it and
 * silently stops giving feedback. Gradient surfaces press by DIMMING — which
 * is also how every dark instrument in Daylight presses, and which removes a
 * pre-existing 1.90:1 white-on-thrive contrast failure that existed for as
 * long as the press was held.
 */
export const CTA_GRADIENT = {
  // DARKENED 2026-08-02 (founder: "the gradient on these 2 cards, to be closer
  // to the dark green above, keep the angle and everything else"). The dark end
  // is now GLASS_ROOTS — the exact colour of the dark instrument card these sit
  // beneath — so the pair reads as one family stepping down in weight instead
  // of a bright card interrupting a dark one. Angle and stops unchanged.
  //
  // Both ends still clear AA for white by a wide margin: 11.67:1 -> 6.33:1.
  colors: [GLASS_ROOTS, CoachCedarColors.cedarPrime] as const,
  start: { x: 0, y: 0 },
  end: { x: 1, y: 1 },
};

/**
 * BUTTON_GRADIENT — the CTA ramp. Runs DARK AT THE TOP to light at the bottom,
 * vertically (founder 2026-08-02: "a dark to light may be better vs the sheen…
 * especially for buttons that are under a darker card, they flow better").
 *
 * The dark end is GLASS_ROOTS — the EXACT colour of the dark instrument cards
 * and drawer headers — so a button sitting under one continues its surface
 * rather than starting a new note beneath it. The gradient does the lighting a
 * sheen would otherwise do, which is why CTAs do NOT stack InstrumentSheen on
 * top: two light sources on one small surface read as a smudge, not as glass.
 *
 * Distinct from CTA_GRADIENT ON PURPOSE — different roles, both approved:
 *   CTA_GRADIENT     hero CARD, diagonal, cedarPrime → CEDAR_BRIGHT
 *   BUTTON_GRADIENT  CTA BUTTON, vertical, GLASS_ROOTS → CEDAR_BRIGHT
 * A card is a surface you read; a button is an object you press.
 *
 * Every point on this ramp clears AA for white: 11.67:1 at the dark end,
 * 4.56:1 at the bright end, and monotonic between — so the label is legible
 * wherever it lands. Pinned by TestButtonGradientIsAccessible.
 */
export const BUTTON_GRADIENT = {
  colors: [GLASS_ROOTS, CEDAR_BRIGHT] as const,
  start: { x: 0, y: 0 },
  end: { x: 0, y: 1 },
};

/**
 * DETAIL_HEADER_GRADIENT — the detail pages' header: white at the top settling
 * into the page's limestone at the bottom (founder 2026-08-03 — "light white to
 * limestone gradient"). A flat 62%-white frost over limestone composites to
 * 1.063:1 and is invisible; a RAMP is visible because it differs from the canvas
 * at the top and only MATCHES it at the bottom edge, so the bar dissolves into the
 * page instead of sitting on it as a slab.
 *
 * It lived in DetailHeader.native.tsx until that file moved to the theme
 * (dark-mode sprint, P2c): the theme's `canvas.header` is this in light.
 */
export const DETAIL_HEADER_GRADIENT = {
  colors: ['#FFFFFF', '#F3F3F3'] as const, // white → limestone (page canvas)
  start: { x: 0, y: 0 },
  end: { x: 0, y: 1 },
};
/** The hairline under that header: an ink line a light page can actually show. */
export const DETAIL_HEADER_EDGE = 'rgba(11,22,20,0.14)';
/**
 * DETAIL_FLOATER — the chat/share pill that straddles the detail header's bottom
 * edge: an opaque white toolbar with a soft green-black lift. It was
 * DetailActionRow's own style until that file moved to the theme (P2c); the
 * theme's `material.floater` is this in light.
 */
export const DETAIL_FLOATER = {
  backgroundColor: '#FFFFFF',
  shadowColor: '#0F281E',
  shadowOffset: { width: 0, height: 2 },
  shadowOpacity: 0.16,
  shadowRadius: 8,
  elevation: 3,
} as const;

/**
 * PILL_GRADIENT — the chat pill's ramp: LIGHT AT THE TOP, deepening downward.
 *
 * The INVERSE of BUTTON_GRADIENT, deliberately. Cedar's chat bubble carries a
 * left bar that ramps GLASS_ROOTS → CEDAR_BRIGHT downward (dark top, bright
 * foot). A pill running the other way meets it: the pill's dark foot sits
 * against the bubble's dark head, so the two dark ends kiss at the seam
 * instead of two bright ends colliding (founder 2026-08-20: "light on top
 * sheen and dark on bottom, to meet the flow of dark to light chat bubble
 * sidebar").
 *
 * ⚠️ THE TOP STOP IS CONTRAST-CAPPED, not chosen for looks. Chat pills carry
 * DIMMED white inks — rgba(255,255,255,0.6) on the secondary line. Measured
 * against candidate tops:
 *     GLASS_BG   #113C2B  0.6-ink 5.49   (today's flat fill)
 *     #144E36             0.6-ink 4.60   <- the ceiling, and what we use
 *     #15563B             0.6-ink 4.23   FAILS
 *     CEDAR_BRIGHT        0.6-ink 2.66   FAILS badly
 * A literal reversal of BUTTON_GRADIENT would have put that secondary line at
 * 2.66:1. The house InstrumentSheen (white 16%) is also too strong here — it
 * lands the same ink at 3.90. Do not lighten the top without first lifting
 * every ink in the pill above 0.6 alpha.
 *
 * The foot is pushed BELOW GLASS_BG instead, which only helps text, and buys a
 * 2.43× luminance range so the ramp actually reads.
 */
export const PILL_CREST = '#144E36';
export const PILL_FOOT = '#0E3123';
export const PILL_GRADIENT = {
  colors: [PILL_CREST, PILL_FOOT] as const,
  start: { x: 0, y: 0 },
  end: { x: 0, y: 1 },
};

/**
 * GLASS_EDGE — the bright hairline every glass surface in Daylight carries and
 * every green CTA was missing. FROST_CARD has one (0.95 white), GLASS_CARD_LG
 * has one (GLASS_HAIRLINE_SOFT); `subscriptionButton` explicitly set
 * `borderWidth: 0`. That lit edge is what reads as THICKNESS — without it a
 * gradient is just paint, which is why the first gradient pass still felt flat
 * ("none of these feel glassy", founder 2026-08-02).
 */
export const GLASS_EDGE = {
  borderWidth: 1,
  borderColor: 'rgba(255,255,255,0.30)',
};

/**
 * CEDAR_FROST — frost, tinted cedar. For the container cards inside the
 * account drawers that currently fill with `cedarLight` and put WHITE text on
 * it (User Agreement, No Sleep Tracking, Safe/encrypted, What to expect,
 * Current Style). White on cedarLight measures 3.00:1 — a hard AA failure —
 * and the saturated fill is what makes those drawers feel heavy and washed at
 * once (founder: "old washed feeling").
 *
 * These are CONTAINERS, not buttons, so they get the quiet treatment: the
 * frost material you already approved, tinted, with dark cedar ink at 10.50:1.
 * Same family as the Settings tiles' well (rgba(31,108,79,0.10)).
 *
 * Pair with CEDAR_FROST_INK for anything written on it — never white.
 */
export const CEDAR_FROST = {
  backgroundColor: 'rgba(31,108,79,0.12)',
  borderWidth: 1,
  borderColor: 'rgba(255,255,255,0.60)',
  borderRadius: 12,
  shadowColor: '#0B1614',
  shadowOffset: { width: 0, height: 3 },
  shadowOpacity: 0.10,
  shadowRadius: 10,
  // elevation 0 — ANDROID ONLY: a 12%-tinted fill is translucent, so an Android
  // elevation shadow draws its inner edge band inside the card (the white/grey
  // inset box). Full mechanism on FROST_CARD below. iOS ignores `elevation`.
  elevation: 0,
};

/** The only legible ink for CEDAR_FROST (10.50:1). White is 3.00:1 — the bug. */
export const CEDAR_FROST_INK = '#113C2B';

/**
 * The press language: things LIGHT UP, they do not dim.
 *
 * This was `{ opacity: 0.85 }` — a dim, which reads as the button receding
 * rather than responding (founder 2026-08-04: "it just presses in, meaning no
 * thrive on press, it's odd"). Dimming a dark instrument makes it flatter,
 * which is the opposite of the feedback a press should give.
 *
 * Thrive on the edge instead, with a scale small enough to feel like give
 * rather than movement. The border COLOUR changes and the width does not, so
 * there is no layout shift on press — which means the base style must already
 * carry a border (GLASS_EDGE does).
 */
export const PRESSED_THRIVE = {
  borderColor: 'rgba(59,212,155,0.85)',
  transform: [{ scale: 0.985 }],
};

/** Kept as the name every CTA already imports; the behaviour is PRESSED_THRIVE. */
export const CTA_GRADIENT_PRESSED = PRESSED_THRIVE;

/**
 * The CARD press: give, without jewellery.
 *
 * Cards briefly carried PRESSED_THRIVE too, and the founder rejected it on
 * sight ("when I press on the cedar report card it still shows a thrive
 * outline"). The distinction that survives: BUTTONS light up on press —
 * thrive edge — because a button is an action and the light is its answer.
 * CARDS are places; a place responds by yielding, so a card presses with
 * scale alone. No border change, no dim (a dim on a dark card reads as the
 * card fading, which was the original complaint).
 */
export const PRESSED_CARD = {
  transform: [{ scale: 0.985 }],
};

/**
 * ── The detail-page card ladder (founder direction, 2026-08-21) ──────────────
 * "Top cards should be like heros and ramp colours, then a nice flow following
 * that." Three tiers, three MATERIALS — not one material at three opacities
 * (the founder rejected exactly that in the first mock):
 *
 *   Tier 1 · HERO      GLASS_CARD_LG + InstrumentSheen — dark, full ramp.
 *                      Cedar speaking. Exactly one per page, at the top.
 *   Tier 2 · EVIDENCE  FROST_CARD + FrostRamp — white, faint top-down ramp,
 *                      keeps its shadow. Sits ON the page.
 *   Tier 3 · REFERENCE RECESSED_CARD — tinted DARKER than the page ground,
 *                      hairline, NO shadow. Sits IN the page.
 *
 * The lifted-vs-recessed axis is what separates 2 from 3 at a glance.
 */
export const RECESSED_CARD = {
  // Darker than the limestone canvas so the card reads as INSET. 0.045 was
  // measured too timid — beside a 253 frost card the ~233 it produced still
  // read as "another white card" (founder). 0.075 lands ~226: unmistakably a
  // well, still far from grey-box territory.
  backgroundColor: 'rgba(11,22,20,0.075)',
  borderWidth: 1,
  borderColor: 'rgba(11,22,20,0.09)',
  borderRadius: 12,
  // NO shadow, deliberately — elevation is tier 2's identity.
};

/** Tier 2's ramp — a whisper of cedar at the crest, fading to the frost.
 *  NOT white: the frost computes to ~253 on the limestone canvas, so a white
 *  crest is arithmetically invisible (measured, founder: "tier 2 looks plain
 *  white"). A 7% cedarPrime crest reads as the hero's green in echo — the
 *  "colour flow" — while moving body ink contrast by less than 0.2:1. */
export const FROST_RAMP = {
  colors: ['rgba(31,108,79,0.07)', 'rgba(31,108,79,0)'] as const,
  start: { x: 0, y: 0 },
  end: { x: 0, y: 1 },
};

export const FROST_CARD = {
  // SOFT, but it has to END somewhere (founder 2026-08-17: "these cards on the
  // my week page fade into the background... more contrast so they look sharp").
  //
  // Measured on the Plan page before this change: ground 204, card 240.7 — a
  // 1.42:1 body separation whose only boundary was a 0.95 WHITE hairline. On a
  // light page that edge is invisible by construction, and it computed LIGHTER
  // than the card's own fill (~252 vs ~241) — so the outermost pixels ramped
  // AWAY from the page and the card faded outward instead of ending.
  //
  // This file already says as much about the pill: a white inner hairline
  // "vanishes on a white wash", which is why the nav pill and the chat composer
  // draw a cedarInk edge. The card family never got the lesson. It has it now,
  // in the same 0.10 ink used by the nav break line and six other chrome edges.
  // Still translucent, still a gentle shadow — soft card, sharp boundary.
  backgroundColor: 'rgba(255,255,255,0.85)',
  borderWidth: 1,
  borderColor: 'rgba(11,22,20,0.10)',
  borderRadius: 14,
  shadowColor: '#0B1614',
  shadowOffset: { width: 0, height: 2 },
  // Tightened with the edge: a smaller, slightly darker pool reads as a crisp
  // lift where a wide faint one reads as haze.
  shadowOpacity: 0.10,
  shadowRadius: 8,
  // elevation 0 — ANDROID ONLY, deliberately (founder, Android, 2026-09-10:
  // "a thick white rectangle inside the glass, under the text, all through the
  // app"). Android draws `elevation` as a system shadow from the view's
  // outline, and RN 0.79's CompositeBackgroundDrawable never sets the outline's
  // alpha, so Android treats this 85%-white card as OPAQUE: it draws the
  // shadow's soft inner edge band just inside the border and skips the middle.
  // Through the translucent fill that band reads as a grey rim around a flat
  // white box. `shadowOpacity` has no Android handler, so the 0.10 tuned above
  // never applied there anyway. iOS ignores `elevation` and keeps the shadow*
  // props above, unchanged. Do not restore until Android can cast a
  // translucency-aware shadow.
  elevation: 0,
};

// RETIRED EXPORTS (2026-07-29 cleanup — zero consumers each):
// - HERO_GRADIENT: the dark hero died in Daylight S2; the auth wall carries
//   its own literal gradient and never consumed the token.
// - SHEET_GLASS: BottomSheet implements the Roots-sheet values directly.
// - splitRgba: its whole consumer class (SVG token washes) was retired with
//   the aurora/blobs. THE LESSON STANDS and is gate-enforced repo-wide:
//   SVG <Stop> IGNORES stop-color's alpha — stop-opacity is the authority.
//   Any future SVG consumer of an rgba token must split it (see
//   test_daylight_material_rules.py::TestSvgAlphaIsHonoured).

/**
 * Absolute fill for a gradient that paints a PADDED card.
 *
 * `StyleSheet.absoluteFill` is NOT edge-to-edge inside a padded parent: Yoga
 * insets absolute children by the parent's padding, so the gradient stops
 * short and leaves an unpainted ring of whatever is underneath — the exact
 * trap InstrumentSheen documents and over-bleeds 24pt to escape. Every card
 * using this pads 12-20pt, so the same 24pt bleed clears all of them, and the
 * card's own `overflow: 'hidden'` clips it back to the rounded corners.
 *
 * Use for gradients mounted as a CHILD of the card. When the LinearGradient
 * *is* the card (style={styles.theCard}), you do not need this.
 */
export const RAMP_FILL = {
  position: 'absolute' as const,
  top: -24,
  left: -24,
  right: -24,
  bottom: -24,
};

/**
 * The page canvas: white at the top, settling to limestone at the bottom.
 *
 * Promoted from ChatScreen's local CHAT_CANVAS_GRADIENT so every tab shares
 * one definition — the page and the nav bar have to agree on where limestone
 * lands, and two copies would drift the first time one was tuned.
 *
 * Mount as an absoluteFill BEHIND content (the container keeps `flex: 1` and
 * goes transparent). A gradient is not a colour, so it cannot live in
 * `backgroundColor`.
 */
/**
 * The bottom stop of the page canvas — deeper than limestone, and the reason
 * the gradient is visible at all.
 *
 * White #FFFFFF to limestone #F3F3F3 is TWELVE code values. Spread over an
 * ~800pt screen that is 0.015 per point: below what the eye resolves, which is
 * why the first cut "just looked all limestone" and the nav bar looked flat
 * (founder 2026-08-04). A gradient needs roughly 20-25 values of travel to
 * read as one.
 *
 * The founder's instinct — darken limestone — was right about the cause and
 * the wrong lever: `limestone` is the canvas token on 100+ surfaces and the
 * ground frost is contrast-tuned against, so moving it moves all of them. A
 * deeper FLOOR for the gradient's bottom stop buys the same visible fall and
 * touches nothing else. Carries a slight green bias (230,234,231) rather than
 * a neutral grey, so the page reads as Cedar's rather than as unconsidered.
 */
// v2 (founder 2026-08-05): "it feels slightly greenish — move in a more
// neutral direction." The green bias (230,234,231) was mine; neutral with a
// whisper of cool reads cleaner under white content: (233,234,235).
export const CANVAS_FLOOR = '#E9EAEB';

export const CANVAS_GRADIENT = {
  // White HOLDS for the top ~40% and then falls (founder 2026-08-05: "we need
  // white to live longer before it starts to fade"). A linear 0->1 fall spread
  // 21 values over a whole screen was still too diffuse to register; pinning a
  // second white stop compresses the same travel into the lower ~58%, which
  // makes the fall itself perceptible. `locations` is part of the token — a
  // mount that drops it silently reverts to the diffuse ramp.
  colors: ['#FFFFFF', '#FFFFFF', CANVAS_FLOOR] as const,
  locations: [0, 0.42, 1] as const,
  start: { x: 0, y: 0 },
  end: { x: 0, y: 1 },
};

export const WALL_GRADIENT = {
  colors: [CoachCedarColors.cedarInk, CoachCedarColors.darkCedar, CoachCedarColors.darkCedar, CoachCedarColors.cedarInk] as const,
  locations: [0, 0.14, 0.86, 1] as const,
  start: { x: 0, y: 0 },
  end: { x: 1, y: 0 },
};

/** The workout share card's ground: the deep green ramp, from the top left down. The card is the same picture in both themes. */
export const SHARE_CARD_GROUND = {
  colors: ['#154634', '#113C2B', '#0E3123'] as const,
  start: { x: 0, y: 0 },
  end: { x: 0.3, y: 1 },
};

/**
 * The nav bar continues the canvas rather than capping it: it picks up at
 * limestone — where the page just arrived — and returns to white at the home
 * indicator. Reading the two together, the page falls to limestone and the
 * bar lifts back out of it, so the seam between them is a colour match rather
 * than an edge.
 *
 * This replaces the 95%-alpha approach, which was a visual no-op: the tab bar
 * sits in flex flow (`isFloating` is false on every tab but one), so content
 * ends ABOVE it and there is nothing behind it to show through.
 */
// UP_SHEEN was retired 2026-08-05, one day after it shipped. It lifted the
// page bottom back to WHITE so it could meet a white-topped nav — but that
// erased the floor the canvas had just fallen to, and once the nav became the
// tinted pane there was nothing left for a white lift to meet. The page now
// ends AT the floor and the deck starts AT the floor: continuous colour, with
// the luminous lip marking the glass edge.

/**
 * Brightest at the TOP, where it meets the page's up-sheen, settling to
 * CANVAS_FLOOR at the home indicator.
 *
 * Inverted 2026-08-04. It previously started at CANVAS_FLOOR and lifted to
 * white, which put the DARKEST part of the bar against the darkest part of
 * the page — two dark edges meeting, which reads as a line even without a
 * border. Bright-meets-bright is what makes the join disappear (founder:
 * "the bottom of the pages and the top of the nav bar should feel seamless").
 */
/**
 * The deck pane: CANVAS_FLOOR at its top — the exact value the page canvas
 * has just fallen to — brightening to white at the home indicator.
 *
 * RE-INVERTED 2026-08-05, and the history matters so nobody flips it a third
 * time on half the picture:
 *   v1  floor -> white   correct against a page that ended at the floor
 *   v2  white -> floor   correct against UP_SHEEN's white page-bottom
 *   v3  floor -> white   UP_SHEEN is retired, the page ends at the floor again
 * Each inversion was right for the page bottom of its day. The invariant is
 * not a direction — it is that THE PANE'S TOP MATCHES THE PAGE'S BOTTOM, and
 * the luminous lip sits on that continuous join as the lit edge of the glass.
 */
export const NAV_GRADIENT = {
  // v4 (founder 2026-08-05): "not seeing a gradient or frost... greenish grey
  // and ugly... needs to be more white." CANVAS_FLOOR as the pane's TOP put
  // the bar's most-visible edge at its greenest, darkest value. The pane is
  // now WHITE-forward — brightest surface on screen, shading only faintly —
  // and the page's darker floor beneath it is what makes the pane read as a
  // lit sheet of glass sitting ON the page. The invariant, restated: the pane
  // must CONTRAST the page bottom; today that contrast is bright-over-dark.
  //
  // v5 (founder 2026-08-05, after the icon-placement fix): "re-add a slight
  // gradient to the nav." v4's #F4F5F6 end was only 11 values off white —
  // effectively flat. The end now settles toward (not onto) CANVAS_FLOOR
  // (#E9EAEB): same neutral +1/+2 channel ramp, ~19 values of travel, so the
  // pane visibly shades while staying the brightest surface on screen. The
  // v4 invariant holds: pane contrasts the page bottom, bright over dark.
  colors: ['#FFFFFF', '#ECEDEE'] as const,
  start: { x: 0, y: 0 },
  end: { x: 0, y: 1 },
};

/**
 * The fused unit's ONE edge ink — the ghosted outline of floating glass.
 *
 * Chat draws the top half of this outline (the composer) and the bottom half
 * (the nav pill) — they are one visual object, so they cannot own separate
 * literals. They did, at 0.12 and 0.08, and the composer additionally carried a
 * per-side `borderTopColor` at 0.10; RN gives per-side colour precedence over the
 * uniform one, and the view then rendered NO left/right border at all. The
 * founder saw it as "the corners have no borders, vs on all other pages"
 * (2026-08-17).
 *
 * It lived in tabBarConfig until the dark-mode sprint. It is a colour token, so
 * it lives with the tokens now: the theme's `line.edge` is this value in light,
 * and tabBarConfig re-exports it under the same name for the chat composer.
 */
export const PILL_EDGE_INK = 'rgba(11,22,20,0.08)';

/**
 * The glass pane's wash over REAL blur, top to bottom (BlurPane).
 *
 * Pass 26 (first real-blur tuning session): the day-one-blind 0.72/0.66 tint
 * smothered the diffusion — the glass read as wash (founder: "not looking so
 * glass like"). Light pure-white tint lets the blur be the material.
 * Pass 30, settled by the null-glass probe: with the pane removed the pill
 * region measured IDENTICAL to the page (236 vs 237) — no hidden painter. The
 * perceived density gap is the SCENE, so the glass gets as thin as label
 * legibility allows — intensity 35, tint 0.18.
 */
export const GLASS_PANE_WASH = ['rgba(255,255,255,0.18)', 'rgba(255,255,255,0.16)'] as const;

/**
 * The glass pane's whole material where the phone cannot blur: a denser wash, so
 * content beneath still ghosts through.
 *
 * Pass 24: PURE WHITE both stops — the F0F1F2 bottom stop put each card's grey
 * end wherever the card happened to sit; on the fused unit the composer's grey
 * end landed mid-unit against the pill's white start = a built-in 13-pt seam
 * (founder caught it on a clean ground).
 */
export const GLASS_PANE_FALLBACK = ['rgba(255,255,255,0.70)', 'rgba(255,255,255,0.70)'] as const;
